import * as t from 'io-ts';
import * as WP from './withPose';
import { LayoutProduct } from './product';
import * as A from 'fp-ts/Array';
import * as O from 'fp-ts/Option';
import { identity, pipe } from 'fp-ts/function';
import { ModaSofa } from '@dora/backpack';
import * as s from 'fp-ts/string';
import * as Arya from '../../components/arya';
import * as Pose from '../pose';

/**  Non-modular Furniture for a Layout document */
export type LayoutFurnitureT = WP.WithPose & {
    configurationTemplateId: string;
    products: Array<LayoutProduct>;
};

export const LayoutFurniture = t.intersection([
    WP.WithPose,
    t.type({ configurationTemplateId: t.string, products: t.array(LayoutProduct) }),
]);

/**
 * Tests the equality of two LayoutFurniture
 *
 *  Equality is defined as Furniture with the same pose, same furnitureSpaceId, and the same
 *  products are equal
 */
export function eq(left: LayoutFurnitureT, right: LayoutFurnitureT): boolean {
    return (
        left.configurationTemplateId == right.configurationTemplateId &&
        WP.eq(left, right) &&
        left.products.length === right.products.length &&
        left.products.every(
            lp => right.products.find(rp => rp.productId === lp.productId) !== undefined,
        )
    );
}

export function getFurniture(
    furnitureList: {
        aryaId: string;
        configuration: O.Option<ModaSofa.Configuration>;
    }[],
    arya: O.Option<Arya.Interface>,
) {
    // grab the sku and pose from each piece of furniture in the furniture list,
    // ignoring modular furniture
    return pipe(
        furnitureList,
        A.filter(f => O.isNone(f.configuration)),
        A.map(({ aryaId }) =>
            pipe(
                O.Do,
                O.bind('arya', () => arya),
                O.bind('configurationTemplateId', ({ arya }) =>
                    arya.configurationTemplateId(aryaId),
                ),
                O.bind('pose', ({ arya }) =>
                    pipe(arya.furniturePose(aryaId), O.map(Pose.serialise)),
                ),
                O.bind('products', ({ arya }) =>
                    pipe(
                        arya.furnitureProducts(aryaId),
                        // We don't want to serialise duplicate products IDs within a furniture into the layout, .
                        // When adding furniture, Arya will handle applying multiples of products based on attachment points
                        // and anything that needs to know what products are in the scene should query Arya directly to get the skus.
                        O.map(A.uniq(s.Eq)),
                        O.map(A.map(productId => ({ productId }))),
                    ),
                ),
                O.map(({ configurationTemplateId, pose, products }) => ({
                    configurationTemplateId,
                    pose,
                    products,
                })),
            ),
        ),
        A.sequence(O.Applicative),
        O.fold(() => [], identity),
    );
}

export function getModularFurniture(
    furnitureList: {
        aryaId: string;
        configuration: O.Option<ModaSofa.Configuration>;
    }[],
    arya: O.Option<Arya.Interface>,
) {
    // grab the sku, pose and shape of modular furniture from the furniture list
    return pipe(
        furnitureList,
        A.filter(f => O.isSome(f.configuration)),
        A.map(f =>
            pipe(
                O.Do,
                O.bind('arya', () => arya),
                O.bind('pose', ({ arya }) =>
                    pipe(arya.furniturePose(f.aryaId), O.map(Pose.serialise)),
                ),
                O.bind('shape', () =>
                    pipe(
                        f.configuration,
                        O.map(config => config.size.shape),
                        O.alt(() => O.some('straight' as ModaSofa.Shape)),
                    ),
                ),
                O.bind('size', () =>
                    pipe(
                        f.configuration,
                        O.map(config => config.size),
                        O.alt(() => O.some({ shape: 'straight', middle: 1 } as ModaSofa.Size.Size)),
                        O.map(ModaSofa.unwrapSize),
                    ),
                ),
                O.bind('interstitialPlacements', () =>
                    pipe(
                        f.configuration,
                        O.map(config => config.interstitialPlacements),
                        O.alt((): O.Option<ModaSofa.InterstitialsT> => O.some({})),
                    ),
                ),
                O.bind('cornerStyle', () =>
                    pipe(
                        f.configuration,
                        O.map(config => config.cornerStyle),
                        O.alt(() => O.some(ModaSofa.DefaultCornerStyle)),
                    ),
                ),
                O.map(({ size, interstitialPlacements, shape, pose, cornerStyle }) => ({
                    size,
                    interstitialPlacements,
                    cornerStyle,
                    shape,
                    pose,
                })),
            ),
        ),
        A.compact,
    );
}
